/**
 * Kopf der Vorgangsseite in der Variante „Salon“ (Antoine, 26.09.2026; Mockup
 * `mockup-vorgangsseite.html`, Abschnitt Salon): Kalenderblatt links, daneben der Titel groß in
 * Newsreader (E5, nur Anzeige: großer Anfangsbuchstabe), Status und „in N Tagen“, der Kunde
 * darunter, rechts die Kopfaktionen (Sprach-Pille, Bearbeiten, Menü „⋯“) und darunter die
 * Faktenkärtchen (Beginn, Gäste, Ort, Angebot).
 *
 * Das Kalenderblatt ist zugleich der Schnellweg zum Datum (Punkt 97, Antoine 23.09.2026: „Das
 * Datum muss direkt oben links eingegeben werden können“): Klick öffnet darunter ein Datumsfeld,
 * gespeichert wird beim Verlassen des Feldes, Escape bricht ab. Test-IDs wie bisher
 * (`kopf-datum`, `kopf-datum-eingabe`).
 */
import { useState, type ReactNode } from "react";
import { KleinFenster, StiftHinweis } from "./KleinFenster";
import { DateInput, Icon, StatusPill } from "../components";
import { anlassAnzeige, PILL_CLASSES, type PillTone, type StatusMeta } from "../format";
import { monatAnzeige, monatTeile } from "../../lib/anfrage-angaben";

export interface VorgangFakt {
  icon: string;
  ton: PillTone;
  label: string;
  wert: string;
  /** Leerer Wert: grau statt fett (E4 „leere Werte grau“). */
  leer?: boolean;
  /** Klickbar, z. B. „Ort ergänzen“ → Eckdaten. */
  onClick?: () => void;
  /** Direkt an der Kachel bearbeiten (Antoine 28.09.2026: „in den Kacheln direkt editierbar“):
   *  Klick öffnet darunter ein kleines Fenster mit genau diesen Feldern — wie das Kalenderblatt.
   *  Hat Vorrang vor `onClick`. */
  bearbeiten?: (schliessen: () => void) => ReactNode;
  testId?: string;
  /** Am Handy über beide Spalten des Rasters (Mockup vorgangskopf-handy, 16.10.2026). Ab sm ohne Wirkung. */
  breit?: boolean;
  /** Nur am Handy (unter sm), z. B. die Kachel „Art“, die dort das Raster füllt. */
  nurHandy?: boolean;
}

/** „geburtstag“ → „Geburtstag“ — nur für die Anzeige, der gespeicherte Anlass bleibt, wie er ist. */
export function titelAnzeige(text: string): string {
  return anlassAnzeige(text) ?? text;
}

function datumTeile(iso: string | null | undefined): { monat: string; jahr: string; tag: string; wochentag: string; wochentagKurz: string } | null {
  const m = iso?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [, jahr = "", monat = "", tag = ""] = m;
  const d = new Date(Number(jahr), Number(monat) - 1, Number(tag));
  if (Number.isNaN(d.getTime())) return null;
  return {
    monat: d.toLocaleDateString("de-DE", { month: "short" }).replace(".", "").toUpperCase(),
    jahr,
    tag: String(d.getDate()),
    wochentag: d.toLocaleDateString("de-DE", { weekday: "long" }),
    wochentagKurz: d.toLocaleDateString("de-DE", { weekday: "short" }).replace(".", ""),
  };
}

/** Kalenderblatt mit Direkt-Eingabe des Datums (Punkt 97). Ohne Tag, aber mit Wunschmonat
 *  (Punkt 130): Jahr oben, Monatsabkürzung groß, „Tag festlegen“ unten. */
function Kalenderblatt({
  datum,
  wunschMonat,
  onSpeichern,
}: {
  datum: string | null | undefined;
  wunschMonat?: string | null;
  onSpeichern: (wert: string) => void;
}) {
  const [offen, setOffen] = useState(false);
  const t = datumTeile(datum);
  const mo = t ? null : monatTeile(wunschMonat);
  return (
    <div className="relative shrink-0 sm:h-full">
      <button
        type="button"
        data-testid="kopf-datum"
        title={t ? "Datum ändern" : mo ? "Tag festlegen" : "Datum eingeben"}
        aria-label={
          t ? `Datum ${t.wochentag}, ${t.tag}. ${t.monat} ${t.jahr} ändern` : mo ? `Wunschmonat ${monatAnzeige(wunschMonat!)}, Tag festlegen` : "Datum eingeben"
        }
        onClick={() => setOffen((v) => !v)}
        className="block w-[62px] overflow-hidden rounded-xl sm:flex sm:h-full sm:flex-col border border-surface-variant bg-surface-container-lowest text-center shadow-[0_2px_6px_rgba(45,38,26,0.06),0_22px_44px_-20px_rgba(45,38,26,0.34)] transition-shadow hover:ring-2 hover:ring-primary/25 dark:border-white/10 dark:bg-night-card dark:shadow-[0_22px_44px_-20px_rgba(0,0,0,0.8)] sm:w-[120px] sm:rounded-2xl"
      >
        <span className="block bg-primary py-[3px] text-[9.5px] font-extrabold tracking-[0.14em] text-on-primary sm:py-1 sm:text-[11px]">
          {t ? (
            <>
              {t.monat}
              <span className="hidden sm:inline"> {t.jahr}</span>
            </>
          ) : mo ? (
            mo.jahr
          ) : (
            "DATUM"
          )}
        </span>
        <span className="block pt-1.5 font-serif text-[30px] font-medium leading-none text-on-background dark:text-inverse-on-surface sm:flex sm:flex-1 sm:items-center sm:justify-center sm:pt-2.5 sm:text-[58px]">
          {t ? (
            t.tag
          ) : mo ? (
            <span data-testid="kopf-datum-monat" className="text-[22px] sm:text-[40px]">
              {mo.kurz}
            </span>
          ) : (
            <Icon name="edit_calendar" className="text-[26px] text-secondary-fixed-dim sm:text-[34px]" />
          )}
        </span>
        <span className="block pb-1.5 pt-0.5 text-[10px] font-bold text-secondary dark:text-secondary-fixed-dim sm:pb-2.5 sm:text-[11.5px]">
          {t ? (
            <>
              <span className="sm:hidden">{t.wochentagKurz}</span>
              <span className="hidden sm:inline">{t.wochentag}</span>
            </>
          ) : mo ? (
            <>
              <span className="sm:hidden">Tag offen</span>
              <span className="hidden sm:inline">Tag festlegen</span>
            </>
          ) : (
            "eintragen"
          )}
        </span>
      </button>
      {offen && (
        <div className="absolute left-0 top-full z-30 mt-2 w-56 rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-3 shadow-2xl dark:border-outline/30 dark:bg-night-card">
          {/* Der Kalender-Knopf im DateInput darf das Feld nicht verlassen — sonst schlösse das
              Verlassen (= Speichern) das Fenster, bevor der Kalender aufgeht. */}
          <label
            className="block font-body-sm text-[12px] font-bold text-secondary dark:text-secondary-fixed-dim"
            onMouseDown={(e) => {
              if ((e.target as HTMLElement).closest('[aria-label="Kalender öffnen"]')) e.preventDefault();
            }}
          >
            Datum
            <DateInput
              autoFocus
              aria-label="Datum"
              data-testid="kopf-datum-eingabe"
              className="mt-1"
              defaultValue={datum?.slice(0, 10) ?? ""}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") setOffen(false);
              }}
              onCommit={(v) => {
                setOffen(false);
                onSpeichern(v);
              }}
            />
          </label>
          <p className="mt-1.5 text-[11px] text-secondary-fixed-dim">Wird beim Verlassen des Feldes gespeichert.</p>
        </div>
      )}
    </div>
  );
}

function Fakt({ fakt }: { fakt: VorgangFakt }) {
  const inhalt = (
    <>
      <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-[9px] sm:h-8 sm:w-8 ${PILL_CLASSES[fakt.ton]}`}>
        <Icon name={fakt.icon} className="text-[16px] sm:text-[18px]" />
      </span>
      <span className="min-w-0">
        <span className="block text-[10px] font-bold uppercase leading-[13px] tracking-[0.07em] text-secondary-fixed-dim sm:text-[10.5px] sm:leading-[14px]">{fakt.label}</span>
        <span
          title={fakt.wert}
          className={`block truncate text-[13px] leading-[19px] sm:text-[14px] ${
            fakt.leer ? "font-medium text-secondary-fixed-dim" : "font-bold text-on-background dark:text-inverse-on-surface"
          } ${fakt.onClick ? "text-primary" : ""}`}
        >
          {fakt.wert}
        </span>
      </span>
    </>
  );
  const rahmen =
    "flex w-full min-w-0 max-w-full items-center gap-2.5 rounded-xl border border-surface-variant bg-surface-container-lowest py-2 pl-2 pr-3 text-left sm:w-auto shadow-[0_1px_2px_rgba(45,38,26,0.05)] dark:border-white/10 dark:bg-night-card sm:py-2 sm:pl-2 sm:pr-3.5";
  if (fakt.bearbeiten) return <FaktMitFenster fakt={fakt} rahmen={rahmen} inhalt={inhalt} />;
  return fakt.onClick ? (
    <button type="button" onClick={fakt.onClick} data-testid={fakt.testId} className={`${rahmen} group hover:border-primary/40`}>
      {inhalt}
      <Icon name="arrow_forward" className="ml-auto shrink-0 pl-1 text-[15px] text-primary opacity-60 transition-opacity group-hover:opacity-100" />
    </button>
  ) : (
    <div data-testid={fakt.testId} className={rahmen}>
      {inhalt}
    </div>
  );
}

/** Kachel mit Klein-Fenster (./KleinFenster.tsx). */
function FaktMitFenster({ fakt, rahmen, inhalt }: { fakt: VorgangFakt; rahmen: string; inhalt: ReactNode }) {
  return (
    <KleinFenster
      testId={fakt.testId}
      className="max-w-full"
      ausloeser={({ offen, umschalten }) => (
        <button
          type="button"
          onClick={umschalten}
          data-testid={fakt.testId}
          aria-expanded={offen}
          title={`${fakt.label} bearbeiten`}
          className={`${rahmen} group w-full hover:border-primary/40 ${offen ? "border-primary/50 ring-2 ring-primary/15" : ""}`}
        >
          {inhalt}
          <StiftHinweis />
        </button>
      )}
    >
      {(schliessen) => fakt.bearbeiten!(schliessen)}
    </KleinFenster>
  );
}

export function VorgangKopf({
  titel,
  status,
  hinweis,
  datum,
  wunschMonat,
  onDatumSpeichern,
  kunde,
  kundeZusatz,
  kontakt,
  rueckweg,
  aktionen,
  fakten,
  nebenStatus,
}: {
  titel: string;
  status: StatusMeta;
  /** „in 6 Tagen“ / „Termin liegt in der Vergangenheit“ neben dem Status. */
  hinweis?: StatusMeta | null;
  datum: string | null | undefined;
  /** Punkt 130: `YYYY-MM`, solange nur der Monat bekannt ist (`wunschMonatVon`). */
  wunschMonat?: string | null;
  onDatumSpeichern: (wert: string) => void;
  kunde: string | null;
  kundeZusatz?: string | null;
  /** Antoine 28.09.2026: hinter dem Namen Telefon und E-Mail, wenn vorhanden, jeweils als Link. */
  kontakt?: { telefon?: string | null; email?: string | null };
  rueckweg?: ReactNode;
  aktionen: ReactNode;
  fakten: VorgangFakt[];
  /** z. B. das Konfetti nach einer Zusage */
  nebenStatus?: ReactNode;
}) {
  const initialen = (kunde ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");
  return (
    <header className="flex flex-col gap-3" data-testid="vorgang-kopf">
      {rueckweg}
      {/* Ein Raster statt doppeltem Markup: am Handy stehen Kopfaktionen und Fakten unter
          Kalenderblatt und Titel über die volle Breite, linksbündig am Inhaltsrand (Antoine
          27.09.2026: eingerückt in der Titelspalte „wirkt unruhig“); ab sm sitzen die Aktionen
          rechts neben dem Titel und die Fakten in der rechten Spalte darunter. Dafür ist die
          Titelzeile am Handy `contents`, ihre beiden Kinder werden eigene Rasterzellen. */}
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3.5 gap-y-3 sm:gap-x-6 sm:gap-y-4">
        {/* Ab sm reicht das Kalenderblatt über Titel UND Fakten-Kacheln bis zu deren
            Unterkante (Antoine 28.09.2026: „bis zur unteren Linie von ‚Uhrzeit offen‘“); dafür
            120 px breit, damit das Seitenverhältnis wie vorher (92×112) bleibt. */}
        <div className="sm:row-span-2 sm:self-stretch">
          <Kalenderblatt datum={datum} wunschMonat={wunschMonat} onSpeichern={onDatumSpeichern} />
        </div>
        <div className="contents min-w-0 flex-wrap items-start justify-between gap-x-4 gap-y-3 sm:flex">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <h1 className="min-w-0 break-words font-serif text-[31px] font-medium leading-none tracking-[-0.01em] text-on-background dark:text-inverse-on-surface sm:text-[40px] xl:text-[44px]">
                {titelAnzeige(titel)}
              </h1>
              <span className="flex flex-wrap items-center gap-2">
                <StatusPill meta={status} testId="event-status-pill" />
                {hinweis && <StatusPill meta={hinweis} />}
                {nebenStatus}
              </span>
            </div>
            {kunde && (
              <p className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-secondary dark:text-secondary-fixed-dim sm:text-[13.5px]">
                <span
                  aria-hidden
                  className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full bg-primary-container text-[9px] font-extrabold text-on-primary-container dark:bg-primary/25 dark:text-[#f0b49b] sm:h-[26px] sm:w-[26px] sm:text-[10px]"
                >
                  {initialen}
                </span>
                <b className="font-bold text-on-background dark:text-inverse-on-surface" data-testid="vorgang-kunde">
                  {kunde}
                </b>
                {kontakt?.telefon && (
                  <a href={`tel:${kontakt.telefon.replace(/[^\d+]/g, "")}`} data-testid="kopf-telefon" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
                    <Icon name="call" className="text-[15px]" />
                    <span className="tabular-nums">{kontakt.telefon}</span>
                  </a>
                )}
                {kontakt?.email && (
                  <a href={`mailto:${kontakt.email}`} data-testid="kopf-email" className="inline-flex min-w-0 items-center gap-1 break-all font-semibold text-primary hover:underline">
                    <Icon name="mail" className="text-[15px]" />
                    {kontakt.email}
                  </a>
                )}
                {kundeZusatz && <span className="hidden sm:inline">· {kundeZusatz}</span>}
              </p>
            )}
          </div>
          <div className="col-span-2 flex flex-wrap items-center justify-between gap-2 sm:justify-start" data-testid="vorgang-aktionen">
            {aktionen}
          </div>
        </div>
        {/* Am Handy ein festes Raster aus zwei gleich breiten Spalten mit Überschrift in jeder Kachel
            (Mockup vorgangskopf-handy, freigegeben 16.10.2026); `breit` geht über beide Spalten.
            Ab sm wie bisher: umbrechende Reihe, ab lg alle Kacheln in EINER Reihe (Antoine
            28.09.2026); lange Werte kürzen, voller Text als Tooltip. */}
        {fakten.length > 0 && (
          <div className="col-span-2 grid min-w-0 grid-cols-2 gap-2 sm:col-span-1 sm:col-start-2 sm:flex sm:flex-wrap sm:gap-2.5 lg:flex-nowrap" data-testid="vorgang-fakten">
            {fakten.map((f) => (
              // Kurze Werte („ab 18:30 Uhr“, „100 Personen“) bleiben ganz, nur lange geben nach.
              <div key={f.label} className={`min-w-0 max-w-full ${f.breit ? "col-span-2" : ""} ${f.nurHandy ? "sm:hidden" : ""} ${f.wert.length <= 18 ? "lg:shrink-0" : ""}`} style={f.wert.length > 18 ? { flexShrink: f.wert.length } : undefined}>
                <Fakt fakt={f} />
              </div>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}
